/**
 * The Medium tier's radiance programs: the uniforms every lit material reads
 * (one set per view, refilled per floor) and the shader hook that samples the
 * baked volume (lifted from the shared engine's `engine/src/radiance.js` hook
 * and the dungeon adapter of `dungeon-radiance.js`; GLSL in
 * `shaders/radiance.ts`).
 */
import type { Theme } from '@ab/content-schema';
import * as THREE from 'three';
import type { ExplorationMask } from './fog.ts';
import type { ShaderHook } from './material-hooks.ts';
import type { Bake } from './radiance.ts';
import { radianceFragmentHeader, radianceLighting, radianceVertex, radianceVertexHeader, rampFog } from './shaders/radiance.ts';

/** The uniforms every radiance program reads; one set per view, refilled per floor. */
export const createRadianceUniforms = () => ({
  hdRadiance: { value: null as THREE.Data3DTexture | null },
  hdRadianceMin: { value: new THREE.Vector3() },
  hdRadianceSpan: { value: new THREE.Vector3(1, 1, 1) },
  dungeonExplored: { value: null as THREE.Texture | null },
  dungeonFloorSize: { value: new THREE.Vector2(1, 1) },
  dungeonUnknownRockGain: { value: 1 },
  dungeonUnknownMoon: { value: 1 },
  dungeonCapSide: { value: 0 },
  dungeonFogDesat: { value: 0 },
  dungeonEdgeMoon: { value: 1 },
  dungeonUnknownLight: { value: new THREE.Vector3() },
  dungeonFogAlbedo: { value: new THREE.Vector3(1, 1, 1) },
  dungeonCapAmbient: { value: new THREE.Vector4() },
});
export type RadianceUniforms = ReturnType<typeof createRadianceUniforms>;

/** Points the shared uniforms at a floor's bake (after its `fill`), mask and theme. */
export const bindRadiance = (u: RadianceUniforms, bake: Bake, mask: ExplorationMask, theme: Theme): void => {
  const r = theme.lightRules;
  u.hdRadiance.value = bake.texture;
  u.hdRadianceMin.value.copy(bake.min);
  u.hdRadianceSpan.value.copy(bake.span);
  u.dungeonExplored.value = mask.texture;
  u.dungeonFloorSize.value.set(bake.span.x, bake.span.z);
  u.dungeonUnknownRockGain.value = r.unknownRockGainPercent / 100;
  u.dungeonUnknownMoon.value = r.unexploredMoonPercent / 100;
  u.dungeonCapSide.value = r.capAmbientSidePercent / 100;
  u.dungeonFogDesat.value = r.unexploredDesaturatePercent / 100;
  u.dungeonEdgeMoon.value = bake.edgeMoon.value;
  u.dungeonUnknownLight.value.fromArray(r.unexploredLightPercent.map((v) => v / 100));
  const fog = new THREE.Color(r.fogHue);
  fog.multiplyScalar(1 / Math.max(1e-4, 0.2126 * fog.r + 0.7152 * fog.g + 0.0722 * fog.b));
  const albedo = new THREE.Color(1, 1, 1).lerp(fog, r.unexploredTintPercent / 100).multiplyScalar(r.unexploredAlbedoPercent / 100);
  u.dungeonFogAlbedo.value.set(albedo.r, albedo.g, albedo.b);
  const cap = new THREE.Color(r.capAmbient).multiplyScalar(r.capAmbientGainPercent / 100);
  u.dungeonCapAmbient.value.set(cap.r, cap.g, cap.b, r.capAmbientFarPercent / 100);
};

/**
 * The radiance hook of a lit Medium material. `ramp` surfaces take the smooth
 * fog albedo; `rock` programs (stone kinds) also take the unexplored-rock gain
 * and the rock-top ambient floor.
 */
export const radianceHook = (u: RadianceUniforms, options: { readonly ramp: boolean; readonly rock: boolean }): ShaderHook => ({
  key: `radiance${options.ramp ? '-ramp' : ''}${options.rock ? '-rock' : ''}`,
  uniforms: options.rock ? u : { ...u, dungeonUnknownRockGain: { value: 1 } },
  ...(options.rock ? { defines: { DUNGEON_ROCK: '1' } } : {}),
  vertexHeader: radianceVertexHeader,
  fragmentHeader: radianceFragmentHeader,
  vertex: [{ chunk: 'worldpos_vertex', after: radianceVertex }],
  fragment: [...(options.ramp ? [{ chunk: 'lights_physical_fragment' as const, before: rampFog }] : []), { chunk: 'lights_fragment_end', after: radianceLighting }],
});
